/* ==========================================================================
   Ozeret -- domain logic
   Pure functions over the records. Every one of these belongs on the server
   eventually; none of them touches React, the DOM, or component state.
   ========================================================================== */

import type {
  Area, Booking, Cleaner, DayState, House, IsoDate, Job, MatchResult, Pair,
  PairLeg, Request, TimeWindow, Weekday, WeekLoad,
} from '../types';
import {
  AREAS, DOW, JOB_CATALOG, LANGUAGES, PAY_METHODS, TRAITS,
} from '../data/catalog';
import { AVAILABILITY, BOOKINGS, CLEANERS, HOUSES, REQUESTS } from '../data/seed';
import { JOB_I18N, getLang, t, tf } from './i18n';
import { TODAY_ISO, addDays, isoDate, minsOf, nowSlot, parseDate } from './time';

/* ------------------------------------------------------------- lookups */

export const byId = <T extends { id: string }>(list: T[], id: string): T | undefined =>
  list.find((x) => x.id === id);

/* The stand-in for an area id nothing matches.

   Its county used to read 'Rockland', from when Ozeret was one county and an
   unknown tag could only plausibly be a Rockland one. That stopped being
   harmless the moment new homes stopped carrying a legacy area at all: a
   household in Crown Heights signed up, had no old-style tag, fell through to
   this, and its own profile told it that it was in Rockland. A placeholder
   that invents a location is worse than one that admits it has none. */
const MISSING_AREA: Area = { id: '?', county: '', label: '', x: 0, y: 0 };

export const area = (id: string): Area => AREAS.find((a) => a.id === id) || MISSING_AREA;
/* An old neighbourhood id gives its label; a town picked by search is already
   words ("Monsey, NY") and used to come back blank. */
export const areaLabel = (id: string): string => area(id).label || id;

/** Where somebody is, in the words they would use.
 *
 *  The postal code is the truth now -- the server turns it into "Brooklyn,
 *  NY" and sends that as `town`. The old twenty-six-neighbourhood tag is only
 *  consulted for accounts made before postal codes were asked for, and an
 *  empty string is the honest answer when there is neither. */
export function townLabel(x: { town?: string; area?: string }): string {
  if (x.town) return x.town;
  if (!x.area) return '';
  const a = area(x.area);
  return a.label ? a.label + (a.county ? ', ' + a.county : '') : '';
}
/** A translated label where the current language has one, else the catalog's. */
const translated = (key: string, fallback: string): string => { const v = t(key); return v === key ? fallback : v; };

export const lang = (id: string): string =>
  translated('tx_lang_' + id, (LANGUAGES.find((l) => l.id === id) || { label: id }).label);
export const job = (id: string): Job =>
  JOB_CATALOG.find((j) => j.id === id) || { id, group: '', label: id, est: 0 };
export const trait = (id: string): string =>
  translated('tx_trait_' + id, (TRAITS.find((x) => x.id === id) || { label: id }).label);
export const payLabel = (id: string): string =>
  translated('tx_pay_' + id, (PAY_METHODS.find((p) => p.id === id) || { label: id }).label);
/** A rating dimension's name, translated where there is one. */
export const dimLabel = (d: { id: string; label: string }): string => translated('tx_dim_' + d.id, d.label);
/** "House", "Apartment"... are stored in English, as typed by the home. */
export const homeTypeLabel = (x: string): string =>
  translated('tx_ht_' + String(x).toLowerCase().replace(/\W+/g, '_'), x);
export const parkingLabel = (x: string): string =>
  translated('tx_park_' + String(x).toLowerCase().replace(/\W+/g, '_'), x + ' parking');

export const cleaner = (id: string): Cleaner => byId(CLEANERS, id)!;
export const house = (id: string): House => byId(HOUSES, id)!;

/** Names for jobs a home invented, gathered from every home we can see.
 *  Refilled on every load, so a custom job reads properly on a request card,
 *  a booking, anywhere -- not just on the list it was created from. */
export const CUSTOM_LABELS: Record<string, string> = {};

export function rebuildCustomLabels(): void {
  for (const k of Object.keys(CUSTOM_LABELS)) delete CUSTOM_LABELS[k];
  for (const h of HOUSES) {
    for (const j of h.jobList) {
      if (j.label) CUSTOM_LABELS[j.jobId] = j.label;
    }
  }
}

/** Catalog labels translate; a home's own words stay as written. */
export function jobLabel(id: string): string {
  if (CUSTOM_LABELS[id]) return CUSTOM_LABELS[id];
  const dict = JOB_I18N[getLang()];
  return (dict && dict[id]) || job(id).label;
}

export const isCustomJob = (id: string): boolean => id.startsWith('custom_');

/** What to call somebody in a sentence. "Marisol Reyes" is Marisol; "The Katz
 *  family" is Katz, not "The" -- which is what naive first-word splitting gave
 *  us, and it read as a bug because it was one. */
export function shortName(full: string): string {
  const cleaned = full.trim().replace(/^(the|la|el)\s+/i, '');
  const first = cleaned.split(/\s+/)[0] ?? cleaned;
  /* "The Bernsteins, New City" is Bernsteins -- not "Bernsteins,". */
  return first.replace(/[,.;:]+$/, '') || full;
}

/* --------------------------------------------------------------- advice */

/** Rough hours for a set of jobs, scaled by home size.
 *  The app SUGGESTS. The people decide. This never sets anything. */
export function suggestHours(jobIds: string[], h?: House): number {
  const base = jobIds.reduce((sum, id) => sum + (job(id).est || 0), 0);
  const size = h ? Math.max(0.7, Math.min(2.2, (h.bedrooms + h.bathrooms) / 5)) : 1;
  return Math.round(base * size * 2) / 2;
}

/* -------------------------------------------------------------- matching */

/** Shape a cleaner is scored against. A real request satisfies it; the
 *  home-side search builds a synthetic one so the number means the same
 *  thing on both sides of the market. */
export interface Scoreable {
  houseId: string;
  jobIds: string[];
  rate: number;
  hours: number;
}

/** Deliberately transparent: we always show the reasons behind the number. */
export function matchScore(
  c: Cleaner, r: Scoreable, viewer: 'house' | 'cleaner' = 'cleaner',
): MatchResult {
  const h = house(r.houseId);
  const reasons: string[] = [];
  let score = 0;

  /* Does this cleaner work where this home is?
   *
   *  Two vocabularies, because two eras. A cleaner's areas used to be legacy
   *  ids ('monsey') and are now towns ('Monsey, NY') chosen from the postal
   *  table. Checking only the old one silently gave every home signed up
   *  since the change a zero here -- the single largest component of the
   *  match score, quietly missing for exactly the newest accounts. */
  const here = townLabel(h);
  if ((h.area && c.areas.includes(h.area)) || (here && c.areas.includes(here))) {
    score += 30;
    reasons.push(t('worksin') + ' ' + (here || areaLabel(h.area)));
  }

  const overlap = r.jobIds.filter((j) => c.specialties.includes(j));
  if (overlap.length) {
    score += Math.min(30, overlap.length * 8);
    /* Whose specialty it is depends on who is reading. A household looking at
       a cleaner was being told "of their jobs are your specialty", which is
       the cleaner's side of the sentence and leaves a family wondering what
       its specialty is supposed to be. */
    reasons.push(overlap.length + ' '
      + t((overlap.length === 1 ? 'jobsspecialty1' : 'jobsspecialty') + (viewer === 'house' ? '_h' : '')));
  }

  const shared = c.languages.filter((cl) => h.languages.some((hl) => hl.id === cl.id));
  if (shared.length) {
    score += 15;
    reasons.push(t('bothspeak') + ' ' + lang(shared[0].id));
  } else {
    score -= 10;
    reasons.push(t('f_nolang'));
  }

  if (r.rate >= c.rate) {
    score += 15;
    reasons.push(t(viewer === 'house' ? 'paysatorabove_h' : 'paysatorabove'));
  } else {
    score -= 5;
    reasons.push(t(viewer === 'house' ? 'belowusual_h' : 'belowusual') + ' $' + c.rate);
  }

  if (r.hours >= c.minHours) score += 10;
  else reasons.push(t(viewer === 'house' ? 'shorterthan_h' : 'shorterthan') + ' ' + c.minHours + 'h');

  if (h.kosherHome && c.traits.includes('kosher_exp')) {
    score += 10;
    reasons.push(t('kosherexp'));
  }

  return { score: Math.max(0, Math.min(100, score)), reasons };
}

/** A cleaner's fit for one home, when there is no real request yet. */
export function fitFor(
  c: Cleaner,
  h: House,
  opts: Partial<Pick<Scoreable, 'jobIds' | 'rate' | 'hours'>> = {},
): MatchResult {
  /* Only a household asks how a cleaner fits its home, so the reasons are
     written from the household's side. */
  return matchScore(c, {
    houseId: h.id,
    jobIds: opts.jobIds || h.jobList.map((j) => j.jobId),
    rate: opts.rate || c.rate,
    hours: opts.hours || 4,
  }, 'house');
}

/* ---------------------------------------------------- calendar resolution */

/** Resolve one date, in priority order:
 *    1. a confirmed booking   -- blocks itself, always wins
 *    2. offPeriods            -- multi-day time away
 *    3. overrides[iso]        -- one date forced off / on / partial
 *    4. weekly                -- the usual week
 */
export function dayState(cleanerId: string, iso: IsoDate): DayState {
  const av = AVAILABILITY[cleanerId];
  if (!av) return { status: 'off', windows: [], blocks: [], reason: 'No calendar' };

  const booking = BOOKINGS.find(
    (b) => b.cleanerId === cleanerId && b.date === iso && b.status === 'confirmed',
  );
  if (booking) return { status: 'booked', windows: [], blocks: [], booking };

  const period = av.offPeriods.find((p) => iso >= p.from && iso <= p.to);
  if (period) return { status: 'off', windows: [], blocks: [], reason: period.reason };

  const ov = av.overrides[iso];
  if (ov && ov.status === 'off') {
    return { status: 'off', windows: [], blocks: [], reason: ov.reason || 'Unavailable' };
  }

  const dow = DOW[parseDate(iso).getDay()] as Weekday;
  let windows: TimeWindow[] = (av.weekly[dow] || []).map((w) => {
    const [from, to] = w.split('-');
    return { from, to };
  });
  if (ov && ov.status === 'on' && ov.windows) windows = ov.windows.slice();
  if (!windows.length) {
    return { status: 'off', windows: [], blocks: [], reason: 'Not a working day' };
  }

  const blocks = (ov && ov.blocks) || [];
  if (!blocks.length) return { status: 'free', windows, blocks: [] };

  // Subtract every blocked range from the working windows.
  let open = windows.slice();
  blocks.forEach((b) => {
    const next: TimeWindow[] = [];
    open.forEach((w) => {
      const ws = minsOf(w.from);
      const we = minsOf(w.to);
      const bs = minsOf(b.from);
      const be = minsOf(b.to);
      if (be <= ws || bs >= we) {
        next.push(w);
        return;
      }
      if (bs > ws) next.push({ from: w.from, to: b.from });
      if (be < we) next.push({ from: b.to, to: w.to });
    });
    open = next;
  });

  return {
    status: open.length ? 'partial' : 'off',
    windows: open,
    blocks,
    reason: blocks[0].reason,
  };
}

export function openHours(cleanerId: string, iso: IsoDate): number {
  const st = dayState(cleanerId, iso);
  if (st.status === 'booked') return 0;
  return st.windows.reduce((sum, w) => sum + (minsOf(w.to) - minsOf(w.from)) / 60, 0);
}

export function bookedHours(cleanerId: string, iso: IsoDate): number {
  const b = BOOKINGS.find(
    (x) => x.cleanerId === cleanerId && x.date === iso && x.status === 'confirmed',
  );
  return b ? b.hours : 0;
}

/** Free enough on this date to be worth showing to a home.
 *  This is what removes any need for a cleaner to "post" availability. */
export function freeOn(cleanerId: string, iso: IsoDate, minHours = 2): boolean {
  const st = dayState(cleanerId, iso);
  if (st.status === 'booked' || st.status === 'off') return false;
  const longest = st.windows.reduce(
    (max, w) => Math.max(max, (minsOf(w.to) - minsOf(w.from)) / 60),
    0,
  );
  return longest >= minHours;
}

/** Free from right now, for long enough to be worth calling about.

    "Today" and "now" are different questions. Somebody free from nine until
    one is free today, and is no use at all to a home asking at half past two.
    So this looks at the windows that are still ahead, and measures the part
    of each that has not already gone. */
export function freeNow(cleanerId: string, minHours = 2): boolean {
  const slot = nowSlot();
  if (slot.date !== TODAY_ISO) return false;

  const st = dayState(cleanerId, TODAY_ISO);
  if (st.status === 'booked' || st.status === 'off') return false;

  const from = minsOf(slot.start);
  const longest = st.windows.reduce((max, w) => {
    const usable = (minsOf(w.to) - Math.max(minsOf(w.from), from)) / 60;
    return Math.max(max, usable);
  }, 0);
  return longest >= minHours;
}

/** The next date this cleaner has a usable block free. */
export function nextFreeDate(
  cleanerId: string,
  fromIso: IsoDate,
  minHours = 3,
  horizon = 21,
): IsoDate | null {
  for (let i = 0; i < horizon; i++) {
    const iso = addDays(fromIso, i);
    if (freeOn(cleanerId, iso, minHours)) return iso;
  }
  return null;
}

/** How much of what you offered is actually sold. */
export function weekLoad(cleanerId: string, mondayIso: IsoDate): WeekLoad {
  const start = parseDate(mondayIso);
  const days = [];
  let open = 0;
  let booked = 0;
  for (let i = 0; i < 7; i++) {
    const iso = isoDate(new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
    const o = openHours(cleanerId, iso);
    const b = bookedHours(cleanerId, iso);
    open += o;
    booked += b;
    days.push({ iso, open: o, booked: b, state: dayState(cleanerId, iso) });
  }
  const capacity = open + booked;
  return { days, open, booked, capacity, pct: capacity ? Math.round((booked / capacity) * 100) : 0 };
}

/* ------------------------------------------------------------- geography */

/** Great-circle miles. The same arithmetic the server does, so a distance
 *  worked out here and one sent down never disagree. */
export function milesApart(
  aLat: number, aLon: number, bLat: number, bLon: number,
): number {
  const rad = (d: number) => (d * Math.PI) / 180;
  const dLat = rad(bLat - aLat);
  const dLon = rad(bLon - aLon);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(rad(aLat)) * Math.cos(rad(bLat)) * Math.sin(dLon / 2) ** 2;
  return 2 * 3958.8 * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Real miles between two places when both are on the map.

 *  The schematic fallback is what Ozeret used when "where" was twenty-six
 *  neighbourhoods drawn on a little grid. It stays for anybody not yet placed
 *  -- a rough number beats a blank -- but it is no longer the answer. */
export function areaDistance(a: string, b: string): number {
  const A = area(a);
  const B = area(b);
  if (A.id === '?' || B.id === '?') return 99;
  return Math.round(Math.hypot(A.x - B.x, A.y - B.y) * 0.9);
}

type Placed = { lat?: number | null; lon?: number | null; area?: string };

/** The three things that locate somebody, pulled off a home or a cleaner. */
export function placeOf(x: { lat?: number | null; lon?: number | null; area?: string }) {
  return { lat: x.lat ?? null, lon: x.lon ?? null, area: x.area ?? '' };
}

/** Miles between two people, however much we happen to know about them. */
export function milesBetweenPlaces(a: Placed, b: Placed): number {
  if (
    typeof a.lat === 'number' && typeof a.lon === 'number' &&
    typeof b.lat === 'number' && typeof b.lon === 'number'
  ) {
    const d = milesApart(a.lat, a.lon, b.lat, b.lon);
    return d < 10 ? Math.round(d * 10) / 10 : Math.round(d);
  }
  return areaDistance(a.area ?? '', b.area ?? '');
}

/** How far this is from the person reading, as a phrase or nothing at all.
 *  Silence is correct when we cannot place one of them: an invented number
 *  about how far somebody has to drive is worse than no number. */
export function milesLabel(miles: number | null | undefined): string {
  if (miles === null || miles === undefined) return '';
  if (miles < 1) return t('tx_few_minutes');
  return miles === 1 ? t('tx_mile_away') : tf('tx_miles_away', { n: miles });
}

/** The same distance, short enough for a chip.
 *
 *  Two homes in one postal code come out as zero, which is arithmetically
 *  true and reads as a glitch. They are not in the same place, we simply
 *  cannot tell them apart at this grain -- so say that instead. */
export function milesChip(miles: number | null | undefined): string {
  if (miles === null || miles === undefined) return '';
  return miles < 1 ? t('tx_nearby') : miles + ' mi';
}

export function nearbyAreas(id: string, within = 6): Area[] {
  return AREAS.filter((a) => a.id !== id && areaDistance(id, a.id) <= within).sort(
    (a, b) => areaDistance(id, a.id) - areaDistance(id, b.id),
  );
}

/** The areas list, arranged around wherever somebody actually is.
 *
 *  The filter used to offer all twenty-nine neighbourhoods in catalog order,
 *  the same for everyone: a home in Monsey scrolled past Bay Ridge and
 *  Sheepshead Bay to reach Airmont, two miles up the road. Ordering it by
 *  distance costs nothing and is the whole of the fix.
 *
 *  Ranked rather than cut at a mile threshold, and deliberately. The area
 *  grid's x/y are schematic -- Monsey to Airmont comes out at about 8 miles
 *  when it is really 3 -- so the numbers overstate by something like double.
 *  What the grid IS reliable about is which of two places is closer, and a
 *  ranking only needs that. A threshold would inherit the error and quietly
 *  drop the neighbours.
 *
 *  Anchors are wherever the reader is: a home's own area, or every area a
 *  cleaner says they work in. With none -- a cleaner who has not filled that
 *  in yet -- there is nothing to sort around, so the whole list comes back
 *  unchanged rather than an arbitrary tenth of it.
 *
 *  `keep` is for areas that are already ticked. A selection you cannot see is
 *  a selection you cannot undo, so those are always in the near group however
 *  far away they are. */
export function areasNear(
  anchors: string[],
  opts: { count?: number; keep?: string[] } = {},
): { near: Area[]; far: Area[] } {
  const { count = 10, keep = [] } = opts;
  const real = anchors.filter((a) => a && area(a).id !== '?');

  /* Nothing to sort around -- a cleaner who has not said where they work.
     The whole list is the near group, not the hidden one: putting it in `far`
     left the picker showing no areas at all until you found the toggle, which
     is worse than the catalog order this set out to fix. */
  if (!real.length) return { near: AREAS, far: [] };

  /* How far an area is from the nearest anchor. An anchor is zero from
     itself, so the reader's own areas sort to the top. */
  const from = (id: string) => Math.min(...real.map((anchor) => areaDistance(anchor, id)));

  const sorted = [...AREAS].sort((x, y) => from(x.id) - from(y.id) || x.label.localeCompare(y.label));
  const nearest = new Set(sorted.slice(0, count).map((a) => a.id));
  for (const id of keep) nearest.add(id);
  for (const id of real) nearest.add(id);

  return {
    near: sorted.filter((a) => nearest.has(a.id)),
    far: sorted.filter((a) => !nearest.has(a.id)),
  };
}

/* ----------------------------------------------------------- job pairing */

/** Two jobs the same day that fit back to back and are close enough to reach.
 *  A half-day gap in the middle of Tuesday is what actually costs a cleaner
 *  money, and nobody else in this market surfaces it. */
export function findPairs(cleanerId: string): Pair[] {
  const commitments: PairLeg[] = BOOKINGS.filter(
    (b) => b.cleanerId === cleanerId && b.status === 'confirmed',
  ).map((b) => ({
    kind: 'booking', ref: b, date: b.date, start: b.start, hours: b.hours,
    areaId: house(b.houseId).area,
    place: placeOf(house(b.houseId)),
  }));

  /* Anything the server already decided is close enough to show them. It has
     the real distances; second-guessing it here with a list of neighbourhood
     names is how a job forty minutes away becomes invisible. */
  const candidates: PairLeg[] = REQUESTS.filter((r) => r.status === 'open').map((r) => ({
    kind: 'request', ref: r, date: r.date, start: r.start, hours: r.hours,
    areaId: house(r.houseId).area,
    place: placeOf(house(r.houseId)),
  }));

  const all = commitments.concat(candidates);
  const pairs: Pair[] = [];

  for (let i = 0; i < all.length; i++) {
    for (let k = i + 1; k < all.length; k++) {
      const a = all[i];
      const b = all[k];
      if (a.date !== b.date) continue;
      if (a.kind === 'booking' && b.kind === 'booking') continue;

      const first = minsOf(a.start) <= minsOf(b.start) ? a : b;
      const second = first === a ? b : a;
      const gap = minsOf(second.start) - (minsOf(first.start) + first.hours * 60);
      const miles = milesBetweenPlaces(first.place, second.place);
      const travel = Math.max(10, miles * 4);

      if (gap < travel) continue;  // physically impossible
      if (gap > 180) continue;     // three hours of dead time is not a pair
      pairs.push({ first, second, gap, miles, travel, date: a.date });
    }
  }
  return pairs.sort((x, y) => x.miles - y.miles || x.gap - y.gap);
}

/* ------------------------------------------------------------- schedules */

export function scheduleFor(cleanerId: string, iso: IsoDate): Booking[] {
  return BOOKINGS.filter(
    (b) => b.cleanerId === cleanerId && b.date === iso && b.status !== 'cancelled',
  ).sort((a, b) => minsOf(a.start) - minsOf(b.start));
}

export function houseScheduleFor(houseIds: string[], iso: IsoDate): Booking[] {
  return BOOKINGS.filter(
    (b) => houseIds.includes(b.houseId) && b.date === iso && b.status !== 'cancelled',
  ).sort((a, b) => minsOf(a.start) - minsOf(b.start));
}

/* -------------------------------------------------------------- requests */

export function liveApplicants(r: Request) {
  return r.applicants.filter((a) => a.status !== 'declined');
}

/** People who actually put themselves forward -- not ones the home invited
 *  who have not answered yet. Counting invites made "1 applied" out of an
 *  invitation nobody had accepted. */
export function appliedCount(r: Request): number {
  return r.applicants.filter((a) => a.status === 'applied' || a.status === 'counter').length;
}
